/**
 * NCR list filter bar — Quality → NCRs filter panel.
 *
 * CR-5550 round 3: Jacq's rework feedback was "Supplier Group missing,
 * Time Period quick filters missing, format must match Dashboard." Round
 * 2's `fv.division &&` gate on Supplier Group hid the dropdown for
 * tenants whose CompanyGroup hierarchy doesn't have a parent division —
 * but on this screen "Supplier Group" filters by ``supplier__group``
 * (the SUPPLIER's group), which is unrelated to the customer's division
 * hierarchy ``fv.division`` probes. CR-5550's spec lists Supplier Group
 * as a mandatory filter, and Dashboard shows it unconditionally too. So
 * the gates are removed here to match Dashboard parity.
 *
 * Quick date range chips (Last 12 Months / This Month / Last Month /
 * Last Quarter / This Year / Last Year) mirror DashboardFilters.tsx so
 * the visual format is identical. Selecting a chip rewrites dateFrom /
 * dateTo; manual edits clear the active chip highlight.
 *
 * CR-6444 moved the fields themselves into `NCRFilterSections`, which
 * groups them under the app-wide `components/filters/FilterSection`
 * primitives (Date Range · Company · Supplier · Classification · NCR
 * Details). This file keeps the option lists, the dropdown data loading
 * and the panel shell; the shell supplies the padding and the spacing
 * between sections, since those sections render `bare`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { NCRFilterSections } from './NCRFilterSections';
import { coreAPI, suppliersAPI, purchasingAPI, fetchAllPages } from '../../lib/api';
import type { CompanyGroup, Site, SupplierProfile, Warehouse } from '../../lib/api';

/**
 * NCR liability options mirror NonConformanceReport.LIABILITY_TYPE_CHOICES
 * on the backend model (IntegerField, value-keyed).
 */
export const LIABILITY_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Not Determined' },
  { value: '1', label: 'Supplier Liability' },
  { value: '2', label: 'Customer Liability' },
  { value: '3', label: 'Shared Liability' },
  { value: '4', label: 'No Liability' },
];

/** Credit/debit claim workflow statuses (NCRCreditProcess.status). */
export const CLAIM_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'requested', label: 'Requested' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'processed', label: 'Processed' },
];

/** NCR workflow statuses (NonConformanceReport.status). */
export const NCR_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'created', label: 'Created' },
  { value: 'ca_submitted', label: 'CA Submitted' },
  { value: 'car_submitted', label: 'CAR Submitted' },
  { value: 'closed', label: 'Closed' },
  { value: 'deleted', label: 'Deleted' },
];

/** Corrective-action ("Action to Resolve") workflow statuses (CorrectiveAction.status). */
const ACTION_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

export interface NCRFiltersState {
  searchTerm: string;
  statusFilter: string;
  dateFrom: string;
  dateTo: string;
  siteFilter: string;
  supplierGroupFilter: string;
  supplierFilter: string;
  commodityGroupFilter: string;
  commodityTypeFilter: string;
  strategicFilter: boolean;
  // Priority-1 NCR list filters.
  partNumberFilter: string;
  regionFilter: string;
  liabilityFilter: string;
  warehouseFilter: string;
  claimStatusFilter: string;
  actionStatusFilter: string;
  collectionDateFrom: string;
  collectionDateTo: string;
}

export interface NCRFiltersBarProps {
  values: NCRFiltersState;
  onChange: (next: Partial<NCRFiltersState>) => void;
  onClear: () => void;
  /** CR-6451: false on the supplier portal — see NCRFilterSections. */
  showStrategicFilter?: boolean;
}

interface CommodityOption { id: number; name: string; commodity_group?: number | null; }

/** Format a Date as YYYY-MM-DD in local time — avoids UTC shift on BST midnight. */
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type QuickRangeId = 'last12Months' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear' | 'lastYear';

/**
 * The list's default window: a rolling 12 months up to today, open-ended
 * (`dateTo` = ''). This is what the NCR list loads with, so the
 * "Last 12 Months (Default)" chip applies exactly the same range — the
 * chip used to apply the previous 12 COMPLETE months (to last month end),
 * which silently hid anything raised this month.
 */
export function getRollingTwelveMonthsRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  return {
    dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())),
    dateTo: '',
  };
}

function getQuickRangeDates(range: QuickRangeId): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'last12Months':
      return getRollingTwelveMonthsRange();
    case 'thisMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
  }
}

const QUICK_RANGE_LABELS: Record<QuickRangeId, string> = {
  last12Months: 'Last 12 Months (Default)',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  lastQuarter: 'Last Quarter',
  thisYear: 'This Year',
  lastYear: 'Last Year',
};

const QUICK_RANGE_ORDER: QuickRangeId[] = ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'];

export default function NCRFiltersBar({
  values, onChange, onClear, showStrategicFilter = true,
}: NCRFiltersBarProps) {
  const [supplierGroups, setSupplierGroups] = useState<CompanyGroup[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityOption[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityOption[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Promise.allSettled so a single 403 (e.g. /core/sites/ has a
      // tenant-scope gate that rejects some viewers) doesn't blank
      // every other dropdown.
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const [groupsRes, sitesRes, suppliersRes, cGroupsRes, cTypesRes, warehousesRes] = await Promise.allSettled([
        coreAPI.getSupplierGroupsForFilter(),
        fetchAllPages<Site>((page) => coreAPI.getSites({ page_size: 100, page })),
        suppliersAPI.getProfiles({ minimal: true, page_size: 1000 }),
        fetchAllPages<CommodityOption>((page) => purchasingAPI.getCommodityGroups({ page_size: 100, page })),
        fetchAllPages<CommodityOption>((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })),
        fetchAllPages<Warehouse>((page) => coreAPI.getWarehouses({ page_size: 100, page })),
      ]);
      if (cancelled) return;
      const pickResults = <T,>(res: PromiseSettledResult<{ data: { results?: T[] } | T[] }>): T[] => {
        if (res.status !== 'fulfilled') return [];
        const d = res.value.data as { results?: T[] } | T[];
        if (Array.isArray(d)) return d;
        return (d.results as T[]) || [];
      };
      // fetchAllPages resolves to the row array itself — no `.data.results` unwrap.
      const pickAll = <T,>(res: PromiseSettledResult<T[]>): T[] =>
        res.status === 'fulfilled' ? res.value : [];
      const logReject = (label: string, res: PromiseSettledResult<unknown>) => {
        if (res.status === 'rejected') {
          console.warn(`NCR filters: ${label} dropdown unavailable —`, (res.reason as { message?: string })?.message || res.reason);
        }
      };
      setSupplierGroups(pickResults<CompanyGroup>(groupsRes));
      setSites(pickAll(sitesRes));
      setSuppliers(pickResults<SupplierProfile>(suppliersRes));
      setCommodityGroups(pickAll(cGroupsRes));
      setCommodityTypes(pickAll(cTypesRes));
      setWarehouses(pickAll(warehousesRes));
      logReject('supplier groups', groupsRes);
      logReject('sites', sitesRes);
      logReject('suppliers', suppliersRes);
      logReject('commodity groups', cGroupsRes);
      logReject('commodity types', cTypesRes);
      logReject('warehouses', warehousesRes);
    })();
    return () => { cancelled = true; };
  }, []);

  // Region (UK/EU origin) is a fixed set backed by Company.uk_eu_status,
  // populated by the supplier quality master data import. This used to be
  // derived from the distinct supplier countries, which left the dropdown
  // permanently empty on installs where no supplier had a country set.
  const regionOptions = useMemo(() => ['UK', 'EU', 'Non-EU'], []);

  // Cascading filter: narrow suppliers to the selected group.
  const filteredSuppliers = useMemo(() => {
    if (!values.supplierGroupFilter) return suppliers;
    const gid = Number(values.supplierGroupFilter);
    return suppliers.filter(s => {
      const groupId = (s.company_details as unknown as { group?: number | null } | undefined)?.group;
      return groupId === gid;
    });
  }, [suppliers, values.supplierGroupFilter]);

  // Narrow commodity types to the selected group.
  const filteredCommodityTypes = useMemo(() => {
    if (!values.commodityGroupFilter) return commodityTypes;
    const gid = Number(values.commodityGroupFilter);
    return commodityTypes.filter(t => t.commodity_group === gid);
  }, [commodityTypes, values.commodityGroupFilter]);

  // Derive which quick chip matches the current dateFrom/dateTo so the
  // active state highlights when the chip-set dates are still in the inputs.
  const activeQuickRange = useMemo<QuickRangeId | null>(() => {
    if (!values.dateFrom && !values.dateTo) return null;
    return QUICK_RANGE_ORDER.find(r => {
      const d = getQuickRangeDates(r);
      return d.dateFrom === values.dateFrom && d.dateTo === values.dateTo;
    }) || null;
  }, [values.dateFrom, values.dateTo]);

  const setQuickRange = (range: QuickRangeId) => {
    const { dateFrom, dateTo } = getQuickRangeDates(range);
    onChange({ dateFrom, dateTo });
  };

  return (
    <div className="border-t bg-card">
      {/* `bare` FilterSections carry no outer margin, so the gap between
          them belongs to this shell — same arrangement as the Deliveries
          panel, whose CardContent spaces its sections. Without it the five
          headings butt straight up against the row above. */}
      <div className="px-6 py-5 space-y-6">
        <NCRFilterSections
          values={values}
          onChange={onChange}
          quickRanges={QUICK_RANGE_ORDER.map((id) => ({ id, label: QUICK_RANGE_LABELS[id] }))}
          activeQuickRange={activeQuickRange}
          onQuickRange={(id) => setQuickRange(id as QuickRangeId)}
          sites={sites}
          warehouses={warehouses}
          supplierGroups={supplierGroups}
          filteredSuppliers={filteredSuppliers}
          commodityGroups={commodityGroups}
          filteredCommodityTypes={filteredCommodityTypes}
          regionOptions={regionOptions}
          liabilityOptions={LIABILITY_OPTIONS}
          claimStatusOptions={CLAIM_STATUS_OPTIONS}
          actionStatusOptions={ACTION_STATUS_OPTIONS}
          showStrategicFilter={showStrategicFilter}
        />

        {/* Actions */}
        <div className="mt-6 pt-6 border-t flex items-center justify-end">
          <Button variant="outline" onClick={onClear} className="text-sm">
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
